import { useMemo, useState } from "react";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    FormControl,
    TextField,
    Select,
    MenuItem,
    DialogActions,
    Button,
} from "@mui/material";

import { DateTime, DurationObjectUnits } from "luxon";

import { luxonUtils } from "utils/luxonUtils";

export interface DateRangeLastUnitOfTimeDialogProps {
    open: boolean;
    onCancel: () => void;
    onSubmit: (startDateTime: DateTime, endDateTime: DateTime) => void;
}

export function DateRangeLastUnitOfTimeDialog(
    props: DateRangeLastUnitOfTimeDialogProps,
) {
    const [timeAmount, setTimeAmount] = useState<number>(1);
    const [unitOfTime, setUnitOfTime] =
        useState<keyof DurationObjectUnits>("minutes");

    const invalidTimeAmount = useMemo(
        () => Number.isNaN(timeAmount) || timeAmount < 1,
        [timeAmount],
    );

    return (
        <Dialog
            open={props.open}
            onClose={props.onCancel}
        >
            <DialogTitle>Date time range</DialogTitle>
            <DialogContent>
                <DialogContentText
                    sx={{
                        paddingBottom: 3,
                    }}
                >
                    Select a date time range from which you want to see the
                    recordings
                </DialogContentText>
                <FormControl
                    variant="standard"
                    sx={{ width: "5ch", marginRight: 1 }}
                >
                    <TextField
                        id="standard-basic"
                        variant="standard"
                        value={timeAmount}
                        type="number"
                        inputProps={{
                            min: 1,
                        }}
                        onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                            setTimeAmount(Number.parseInt(event.target.value));
                        }}
                        error={invalidTimeAmount}
                    />
                </FormControl>
                <FormControl
                    variant="standard"
                    sx={{ width: "15ch" }}
                >
                    <Select
                        labelId="unit-of-time-label"
                        id="unit-of-time"
                        onChange={(event) =>
                            setUnitOfTime(
                                event.target.value as keyof DurationObjectUnits,
                            )
                        }
                        value={unitOfTime}
                        variant="standard"
                    >
                        <MenuItem
                            value={"minutes" as keyof DurationObjectUnits}
                        >
                            minute(s)
                        </MenuItem>
                        <MenuItem value={"hours" as keyof DurationObjectUnits}>
                            hour(s)
                        </MenuItem>
                        <MenuItem value={"days" as keyof DurationObjectUnits}>
                            day(s)
                        </MenuItem>
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onCancel}>Cancel</Button>
                <Button
                    disabled={invalidTimeAmount}
                    onClick={() => {
                        const endDateTime =
                            luxonUtils.resetSecondsAndMilliseconds(
                                DateTime.now(),
                            );
                        const startDateTime = endDateTime.minus({
                            [unitOfTime]: timeAmount,
                        });

                        props.onSubmit(startDateTime, endDateTime);
                    }}
                >
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
}
